import { Link, useSearchParams } from "react-router-dom"
import { Layout } from "../components/Layout"
import { EmptyState } from "@/components/EmptyState/EmptyState"
import { ErrorFallback } from "@/components/ErrorBoundary/ErrorBoundary"
import { useCommsMessages, ActivityMessage, formatAge } from "@/hooks/useComms"
import { EnvelopeIcon, PaperClipIcon } from "@heroicons/react/24/outline"

function DirectionChip({ direction }: { direction: "inbound" | "outbound" }) {
  const outbound = direction === "outbound"
  return (
    <span
      className={`shrink-0 rounded-full px-2 py-0.5 text-xs font-medium ${
        outbound ? "bg-blue-100 text-blue-800" : "bg-green-100 text-green-800"
      }`}
    >
      {outbound ? "sent" : "received"}
    </span>
  )
}

function MessageRow({ m }: { m: ActivityMessage }) {
  const subject = m.subject || m.thread?.subject || "(no subject)"
  const preview = m.one_line_summary || m.snippet
  const inner = (
    <div className="flex items-start gap-3 px-4 py-3">
      <DirectionChip direction={m.direction} />
      <div className="min-w-0 flex-1">
        <div className="flex items-baseline justify-between gap-2">
          <p className="truncate text-sm font-medium text-primary">{m.from_email}</p>
          <span className="shrink-0 text-xs text-muted-foreground">{formatAge(m.sent_at)}</span>
        </div>
        <p className="truncate text-sm text-primary">{subject}</p>
        {preview && <p className="mt-0.5 truncate text-xs text-muted-foreground">{preview}</p>}
      </div>
      {m.has_attachments && (
        <PaperClipIcon className="mt-0.5 h-4 w-4 shrink-0 text-muted-foreground" />
      )}
    </div>
  )
  // Every message belongs to a thread, but guard anyway so an orphaned row still
  // renders (just non-clickable) instead of linking to undefined.
  return m.thread ? (
    <Link to={`/comms/threads/${m.thread.public_id}`} className="block hover:bg-muted">
      {inner}
    </Link>
  ) : (
    <div className="block">{inner}</div>
  )
}

// The three feed views, mirrored in the ?bucket= URL param so the triage tile can
// deep-link straight into "needs_classify".
const VIEWS = [
  { key: "active", label: "Active", bucket: undefined },
  { key: "needs_classify", label: "Needs classify", bucket: "needs_classify" as const },
  { key: "not_relevant", label: "Not relevant", bucket: "not_relevant" as const },
] as const
type ViewKey = (typeof VIEWS)[number]["key"]

export function CommsMessages() {
  const [searchParams, setSearchParams] = useSearchParams()
  const param = searchParams.get("bucket")
  const view: ViewKey = VIEWS.some((v) => v.key === param) ? (param as ViewKey) : "active"
  const setView = (key: ViewKey) => {
    setSearchParams(key === "active" ? {} : { bucket: key }, { replace: true })
  }
  const activeView = VIEWS.find((v) => v.key === view) ?? VIEWS[0]
  const { data, isLoading, error } = useCommsMessages({
    limit: 100,
    ...(activeView.bucket ? { bucket: activeView.bucket } : {}),
  })

  if (error) {
    return (
      <Layout>
        <ErrorFallback error={error as Error} />
      </Layout>
    )
  }

  const messages = data?.messages ?? []
  const total = data?.total ?? messages.length
  const subtitle = isLoading
    ? "Loading…"
    : total > messages.length
      ? `Showing the latest ${messages.length} of ${total}`
      : `${total} captured ${total === 1 ? "message" : "messages"}, newest first`

  return (
    <Layout>
      <div className="space-y-6">
        <div>
          <h1 className="text-2xl font-bold text-primary">Comms · Activity</h1>
          <p className="text-muted-foreground">{subtitle}</p>
        </div>

        {/* Active / Needs classify / Not relevant toggle */}
        <div className="inline-flex rounded-lg border border-border bg-surface p-0.5 text-sm">
          {VIEWS.map((v) => (
            <button
              key={v.key}
              type="button"
              onClick={() => setView(v.key)}
              className={`rounded-md px-3 py-1.5 font-medium transition-colors ${
                view === v.key
                  ? "bg-primary text-contrast"
                  : "text-muted-foreground hover:text-primary"
              }`}
            >
              {v.label}
            </button>
          ))}
        </div>

        {isLoading ? (
          <div className="space-y-2">
            {Array.from({ length: 8 }).map((_, i) => (
              <div key={i} className="h-16 animate-pulse rounded-lg bg-muted" />
            ))}
          </div>
        ) : messages.length === 0 ? (
          <EmptyState
            icon={<EnvelopeIcon className="h-12 w-12" />}
            title={
              view === "not_relevant"
                ? "Nothing quarantined"
                : view === "needs_classify"
                  ? "All classified"
                  : "No messages yet"
            }
            description={
              view === "not_relevant"
                ? "Messages whose contact or thread is marked “Not relevant” appear here."
                : view === "needs_classify"
                  ? "Every captured message has been classified. Nothing waiting on comms-sync."
                  : "Captured inbound and outbound email will appear here."
            }
          />
        ) : (
          <ul className="divide-y divide-border rounded-lg border border-border bg-surface">
            {messages.map((m) => (
              <li key={m.public_id}>
                <MessageRow m={m} />
              </li>
            ))}
          </ul>
        )}
      </div>
    </Layout>
  )
}
